import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { IMAGES } from "../data/site";
import { useContent } from "@/context/ContentContext";
import { resolveImg } from "@/lib/api";
import { Reveal } from "./Reveal";

export const About = () => {
  const { content } = useContent();
  const ownerImg = content?.images?.owner || IMAGES.owner;
  const coownerImg = content?.images?.coowner || IMAGES.coowner;
  return (
    <section id="about" data-testid="about-section" className="bg-cream py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
        <div>
          <Reveal>
            <span className="font-heading text-gold-dark uppercase tracking-[0.3em] text-sm">
              — Our Story
            </span>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-5 font-heading text-4xl md:text-5xl font-semibold text-charcoal leading-tight">
              Every plate is made <span className="font-script text-burgundy text-5xl md:text-6xl">fresh</span>, the way family does it.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-7 font-body text-lg text-charcoal/75 leading-relaxed">
              At Rainey's Soul Food, every meal is soul food with a Caribbean flair —
              authentic recipes that bring families together. From slow-cooked oxtails to
              smothered turkey wings, every plate is made fresh with homemade flavor and
              genuine Southern hospitality.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-9 flex flex-wrap gap-8">
              {[
                { k: "100%", v: "Homemade Recipes" },
                { k: "Family", v: "Owned & Operated" },
                { k: "Fresh", v: "Cooked Daily" },
              ].map((s) => (
                <div key={s.v}>
                  <div className="font-heading text-3xl text-burgundy">{s.k}</div>
                  <div className="font-body text-sm text-charcoal/60 uppercase tracking-wider mt-1">
                    {s.v}
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <Link
              data-testid="about-read-story-btn"
              to="/about"
              className="mt-9 inline-flex items-center gap-2 group font-body font-semibold text-burgundy hover:text-burgundy-dark transition-colors"
            >
              Read Chef Rainey's Story
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="relative">
          <div className="absolute -inset-3 border border-gold/50 rounded-[2rem] translate-x-4 translate-y-4" />
          {/* Chef Rainey — primary */}
          <img
            src={resolveImg(ownerImg)}
            alt="Chef Rainey, owner of Rainey's Soul Food, in her kitchen"
            loading="lazy"
            className="relative rounded-[2rem] w-full h-[440px] md:h-[560px] object-cover object-top shadow-[0_20px_40px_rgba(0,0,0,0.2)]"
          />
          <div className="absolute bottom-5 left-5 bg-cream/95 backdrop-blur-sm px-5 py-3 rounded-2xl shadow-lg">
            <p className="font-script text-2xl text-burgundy leading-none">Chef Rainey</p>
            <p className="font-body text-[0.65rem] uppercase tracking-[0.2em] text-gold-dark mt-1">
              Founder &amp; Owner
            </p>
          </div>

          {/* Quinton Rainey — secondary co-owner inset */}
          <div
            data-testid="about-coowner"
            className="absolute top-5 right-5 w-28 sm:w-32 lg:w-36 z-10"
          >
            <div className="rounded-2xl overflow-hidden border-4 border-cream shadow-[0_12px_28px_rgba(0,0,0,0.35)]">
              <img
                src={resolveImg(coownerImg)}
                alt="Quinton Rainey, co-owner of Rainey's Soul Food"
                loading="lazy"
                className="w-full h-32 sm:h-36 lg:h-44 object-cover object-top"
              />
            </div>
            <div className="mt-2 bg-cream/95 backdrop-blur-sm rounded-xl px-3 py-1.5 shadow-lg text-center">
              <p className="font-script text-lg text-burgundy leading-none">Quinton Rainey</p>
              <p className="font-body text-[0.55rem] uppercase tracking-[0.18em] text-gold-dark mt-1">
                Co-Owner
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
